import { type FetchResult, useApolloClient } from '@apollo/client';
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  IconCalendarAlt,
  IconGift,
  IconQuestion,
  IconSpecializationStrategy,
  IconStore,
  MINUTE,
} from '@leland-dev/leland-ui-library';
import { type DocumentNode } from 'graphql';
import React, {
  type FC,
  type ReactElement,
  type SVGProps,
  useEffect,
  useState,
} from 'react';

import {
  LelandErrorCode,
  ReferralType,
  type Scalars,
} from '../../../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../../../context/AuthContext';
import phog from '../../../../utils/analytics/posthog/posthog';
import { getCoachUrl } from '../../../../utils/coach';
import useCopyToClipboardWithToast from '../../../../utils/hooks/useCopyToClipboardWithToast';
import { trackFirstMessageSent } from '../../../../utils/message';
import { wasMutationSuccessful } from '../../../../utils/mutations';
import {
  trackCoachingSessionScheduled,
  trackIntroCallScheduled,
} from '../../../../utils/session';
import { getCustomerReferralUrl } from '../../../../utils/url';
import { useReferralsUserQuery } from '../../../referrals/__generated-gql-types__/referrals.generated';
import { type ScheduleData, SessionOption } from '../ScheduleModal';

import {
  BaseScheduleSubmissionModalLayout,
  type BaseScheduleSubmissionModalLayoutProps,
} from './BaseScheduleSubmissionModalLayout';
import {
  useSchedulingSuccessModalAssignPendingOrderMutation,
  useSchedulingSuccessModalCreateCoachingSessionByApplicantMutation,
  useSchedulingSuccessModalRequestIntroMessageMutation,
  useSchedulingSuccessModalUpdateCoachingSessionMutation,
} from './__generated-gql-types__/SchedulingSuccessModal.generated';

interface SchedulingSuccessModalProps
  extends BaseScheduleSubmissionModalLayoutProps {
  categorySlug: Possible<string>;
  refetchQueries?: DocumentNode[];
  setOpen: (open: boolean) => void;
  schedulingData: ScheduleData;
  onSchedule?: (session: {
    id?: Scalars['CoachingSessionUrn']['output'];
    startAt?: number;
  }) => void;
}

const INTRO_ERROR_CODES = [
  LelandErrorCode.TOO_MANY_INTRO_CALLS_WITH_COACH,
  LelandErrorCode.TOO_MANY_COACH_REQUESTS,
];

export const SchedulingSuccessModal: FC<SchedulingSuccessModalProps> = ({
  setOpen,
  categorySlug,
  onBackToStart,
  refetchQueries,
  onSchedule,
  schedulingData,
  ...baseScheduleSubmissionModalLayoutProps
}) => {
  const apolloClient = useApolloClient();
  const coachProfileLink = getCoachUrl({
    coachSlug: schedulingData.coach.slug,
    categorySlug,
  });
  const { data: userData } = useReferralsUserQuery();
  const referralCode = userData?.user?.applicant?.referralCode;
  const shareableLink = getCustomerReferralUrl(
    referralCode ?? '',
    ReferralType.CUSTOMER_REFER_CUSTOMER,
  );
  const copyLink = useCopyToClipboardWithToast();

  const { isScheduling, schedulingError, scheduledSessionId } =
    useSchedulingLogic({
      schedulingData,
      onSchedule,
    });

  return (
    <BaseScheduleSubmissionModalLayout
      buttonProps={{
        label: 'Sounds good!',
        size: ButtonSize.LARGE,
        width: ButtonWidth.FULL,
        onClick: () => setOpen(false),
        disabled: isScheduling,
      }}
      sessionType={schedulingData.sessionType}
      preventScroll
      sidebarOptions={{
        title: isScheduling
          ? 'Scheduling your session...'
          : schedulingError
            ? 'Scheduling Error'
            : 'Your session was scheduled.',
        subtitle: isScheduling
          ? 'Please wait a moment while we confirm your session.'
          : schedulingError
            ? schedulingError
            : 'We’ve got a date — don’t miss it! Missing this call may limit your ability to book future sessions. A calendar invitation has been sent to your email.',
        showAddToCalendar: !isScheduling && !schedulingError && true,
      }}
      onBackToStart={onBackToStart}
      scheduledSessionId={scheduledSessionId}
      showBackButton={false}
      {...baseScheduleSubmissionModalLayoutProps}
    >
      <div className="flex size-full min-h-fit flex-col items-center justify-start gap-3 bg-leland-gray-hover p-6 text-center text-lg">
        {isScheduling ? (
          <div className="flex flex-col items-center gap-2">
            <p className="text-lg font-medium text-leland-gray-dark">
              Processing your request...
            </p>
          </div>
        ) : null}
        {schedulingError ? (
          <div className="flex w-full flex-col gap-2 rounded-lg border border-leland-red bg-red-50 p-5 text-left text-leland-red">
            <p className="font-medium">Error Scheduling Session</p>
            <p>{schedulingError}</p>
            <p>
              Please go back and try again, or contact support if the issue
              persists.
            </p>
            <Button
              buttonColor={ButtonColor.RED}
              label="Go Back"
              onClick={() => setOpen(false)}
            />
          </div>
        ) : null}
        {!isScheduling && !schedulingError && (
          <>
            {schedulingData.sessionType === SessionOption.INTRO_CALL ? (
              <div className="flex w-full flex-col gap-2 rounded-lg border bg-leland-white p-5 text-left">
                <p className="text-sm font-medium text-leland-blue">
                  What&apos;s next?
                </p>
                <p className="font-medium">Make the most of your intro call</p>
                <p>
                  {`Use this 15-minute call to decide whether ${schedulingData.coach.user.firstName} is a good coaching fit for you. Here are a few tips to help you prepare for the call:`}
                </p>
                <div className="flex flex-col">
                  <TextWithIcon
                    Icon={IconStore}
                    title={
                      <p className="font-medium">
                        Take a look at {schedulingData.coach.user.firstName}’s{' '}
                        <a
                          href={coachProfileLink}
                          className="underline"
                          target="_blank"
                        >
                          profile & coaching offerings
                        </a>
                      </p>
                    }
                    text="Which options fit your budget and your timeline?"
                  />
                  <TextWithIcon
                    Icon={IconQuestion}
                    title={<p className="font-medium">Bring some questions</p>}
                    text={`Consider asking about ${schedulingData.coach.user.firstName}’s working style, expertise, or availability.`}
                  />
                  <TextWithIcon
                    Icon={IconSpecializationStrategy}
                    title={
                      <p className="font-medium">
                        Come ready to share your goals and challenges
                      </p>
                    }
                    text={`${schedulingData.coach.user.firstName} will want to learn about you too!`}
                  />
                </div>
              </div>
            ) : null}
            {schedulingData.sessionType === SessionOption.INTRO_CALL ? (
              <div className="flex w-full flex-col gap-2 rounded-lg border bg-leland-white p-5 text-left">
                <p className="font-medium">Looking for help right away?</p>
                <p>
                  {`Intro calls aren’t coaching sessions. If you have specific advice-oriented questions for ${schedulingData.coach.user.firstName}, you can book a coaching session instead.`}
                </p>
                <div className="mt-4">
                  <Button
                    buttonColor={ButtonColor.BLACK}
                    label="Book a coaching session"
                    LeftIcon={IconCalendarAlt}
                    onClick={async () => {
                      await apolloClient.refetchQueries({
                        include: refetchQueries,
                      });
                      onBackToStart();
                      phog.capture(
                        'schedule_modal--book_time_immediately_after_scheduling_intro_call',
                      );
                    }}
                  />
                </div>
              </div>
            ) : null}
            {schedulingData.sessionType === SessionOption.COACHING_SESSION ? (
              <div className="flex w-full flex-col gap-2 rounded-lg border bg-leland-white p-5 text-left">
                <div className="flex flex-row gap-1">
                  <IconGift className="size-4 shrink-0 text-leland-primary" />
                  <p className="text-sm font-medium text-leland-primary">
                    Refer a friend
                  </p>
                </div>
                <p className="font-medium">
                  {`Get $50 off your next session with ${schedulingData.coach.user.firstName}`}
                </p>
                <p>
                  Know someone who could use Leland? When you refer your friends
                  to Leland and they purchase, you’ll both get $50 in Leland
                  credit.
                </p>
                <p className="mt-6 font-medium">Your unique referral link</p>
                <div className="flex w-full items-start justify-between rounded-lg border border-leland-gray-stroke bg-white">
                  <div className="p-4">
                    <p className="line-clamp-1 break-all  text-leland-gray-extra-light">
                      {shareableLink}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="flex h-full items-center justify-center whitespace-nowrap p-4 text-leland-primary"
                    onClick={() => copyLink(shareableLink)}
                  >
                    Copy link
                  </button>
                </div>
              </div>
            ) : null}
            <div className="flex items-center bg-leland-white"></div>
          </>
        )}
      </div>
    </BaseScheduleSubmissionModalLayout>
  );
};

interface TextWithIconProps {
  Icon: React.FC<SVGProps<SVGSVGElement>>;
  title: ReactElement;
  text: string;
}

const TextWithIcon: FC<TextWithIconProps> = ({ Icon, title, text }) => {
  return (
    <div className="flex flex-row gap-4 py-3">
      {<Icon className="mt-1 size-5 shrink-0" />}
      <div className="flex flex-col">
        {title}
        <p>{text}</p>
      </div>
    </div>
  );
};

interface UseSchedulingLogicProps {
  schedulingData: ScheduleData;
  onSchedule?: (session: {
    id?: Scalars['CoachingSessionUrn']['output'];
    startAt?: number;
  }) => void;
}

const useSchedulingLogic = ({
  schedulingData,
  onSchedule,
}: UseSchedulingLogicProps) => {
  const { currentUser } = useAuth();

  const isRescheduling = !!schedulingData.coachingSession;
  const isIntroCall = schedulingData.sessionType === SessionOption.INTRO_CALL;

  const [isScheduling, setIsScheduling] = useState(true);
  const [schedulingError, setSchedulingError] =
    useState<Possible<string>>(null);
  const [scheduledSessionId, setScheduledSessionId] = useState<
    Scalars['CoachingSessionUrn']['output'] | undefined
  >(undefined);

  const [createCoachingSessionByApplicantAction] =
    useSchedulingSuccessModalCreateCoachingSessionByApplicantMutation();
  const [updateCoachingSession] =
    useSchedulingSuccessModalUpdateCoachingSessionMutation();
  const [requestIntroMessageAction] =
    useSchedulingSuccessModalRequestIntroMessageMutation();
  const [assignPendingOrderAction] =
    useSchedulingSuccessModalAssignPendingOrderMutation();

  const formatCoachingSession = (time: number, title: string) => {
    return {
      startAt: time,
      endAt: time + schedulingData.sessionLength * MINUTE,
      oneOnOneSession: {
        title,
      },
    };
  };

  const formatCoachingSessionTitle = () =>
    `${schedulingData.coach.user.firstName} <> ${currentUser?.firstName} (Leland ${
      isIntroCall ? 'Intro Call' : 'Coaching Session'
    })`;

  const performScheduling = async () => {
    setIsScheduling(true);
    setSchedulingError(null);

    try {
      if (isIntroCall && !isRescheduling) {
        // --- Intro Call Scheduling Logic ---
        const coachingSessionForIntro = schedulingData.selectedTime
          ? {
              coachUrn: schedulingData.coach.id,
              ...formatCoachingSession(
                schedulingData.selectedTime,
                formatCoachingSessionTitle(),
              ),
            }
          : undefined;

        const introCallRes = await requestIntroMessageAction({
          variables: {
            coachUrn: schedulingData.coach.id,
            message:
              schedulingData.message ??
              `Hi! I'd like to schedule an intro session.`,
            categoryUrn: schedulingData.categoryUrn,
            introCallRequested: !!schedulingData.selectedTime,
            coachingSession: coachingSessionForIntro,
          },
        });

        if (!wasMutationSuccessful(introCallRes)) {
          setSchedulingError(
            (INTRO_ERROR_CODES.includes(
              introCallRes.errors?.[0].extensions?.errorCode as LelandErrorCode,
            ) &&
              (introCallRes.errors?.[0].extensions
                ?.message as Optional<string>)) ||
              'Error setting up intro session. Please try again. If the problem persists, please contact support.',
          );
          return;
        } else {
          const scheduledSession =
            introCallRes.data?.requestIntroMessage?.coachingSessions?.[0];
          if (scheduledSession) {
            trackIntroCallScheduled({
              coachUrn: schedulingData.coach.id,
              sessionUrn: scheduledSession.id,
              startAt: scheduledSession.startAt,
              createdAt: scheduledSession.createdAt,
            });
            trackFirstMessageSent({
              coachUrn: schedulingData.coach.id,
            });
            setScheduledSessionId(scheduledSession.id);
            onSchedule?.({
              id: scheduledSession.id,
              startAt: scheduledSession.startAt,
            });
          }
        }
      } else {
        // --- Non-Intro Session (Coaching Session or Rescheduling) Logic ---
        if (!schedulingData.selectedTime) {
          setSchedulingError('A time was not selected for the session.');
          return;
        }

        if (schedulingData.orderToAssign) {
          await assignPendingOrderAction({
            variables: {
              coachUrn: schedulingData.coach.id,
              orderUrn: schedulingData.orderToAssign,
            },
          });
        }

        let res: FetchResult;
        let newScheduledSessionId:
          | Scalars['CoachingSessionUrn']['output']
          | undefined;

        let newSessionStartTime: number | undefined;

        if (isRescheduling) {
          if (!schedulingData.coachingSession) {
            console.error('Error: coachingSession is missing!');
            return;
          }
          res = await updateCoachingSession({
            variables: {
              id: schedulingData.coachingSession?.id,
              input: formatCoachingSession(
                schedulingData.selectedTime,
                schedulingData.coachingSession?.content.__typename ===
                  'OneOnOneSession'
                  ? schedulingData.coachingSession.content.title
                  : formatCoachingSessionTitle(),
              ),
              message: schedulingData.message,
              recurringSessionInput:
                schedulingData.recurringCadence &&
                !schedulingData.justThisSession
                  ? {
                      cadence: {
                        dayOfWeek: schedulingData.recurringCadence.dayOfWeek,
                        interval: schedulingData.recurringCadence.interval,
                        type: schedulingData.recurringCadence.type,
                        weekOfMonth:
                          schedulingData.recurringCadence.weekOfMonth,
                        isLastWeekOfMonth:
                          schedulingData.recurringCadence.isLastWeekOfMonth ??
                          false,
                      },
                    }
                  : undefined,
            },
          });
          newScheduledSessionId = schedulingData.coachingSession?.id;
          newSessionStartTime = schedulingData.coachingSession?.startAt;
        } else {
          const coachingSessionRes =
            await createCoachingSessionByApplicantAction({
              variables: {
                createCoachingSessionByApplicantInput: {
                  coachUrn: schedulingData.coach.id,
                  ...formatCoachingSession(
                    schedulingData.selectedTime,
                    formatCoachingSessionTitle(),
                  ),
                },
                recurringSessionInput: schedulingData.recurringCadence
                  ? {
                      cadence: {
                        dayOfWeek: schedulingData.recurringCadence.dayOfWeek,
                        interval: schedulingData.recurringCadence.interval,
                        type: schedulingData.recurringCadence.type,
                        weekOfMonth:
                          schedulingData.recurringCadence.weekOfMonth,
                        isLastWeekOfMonth:
                          schedulingData.recurringCadence.isLastWeekOfMonth ??
                          false,
                      },
                      coachUrn: schedulingData.coach.id,
                    }
                  : undefined,
                categoryUrn: schedulingData.categoryUrn,
                message: schedulingData.message,
              },
            });

          const createdSession =
            coachingSessionRes.data?.createCoachingSessionByApplicant;
          if (createdSession) {
            trackCoachingSessionScheduled({
              attendees: (createdSession.attendees ?? []).map(
                (attendee) => attendee.id,
              ),
              coachUrn: schedulingData.coach.id,
              sessionUrn: createdSession.id,
              startAt: createdSession.startAt,
            });
            newScheduledSessionId = createdSession.id;
            newSessionStartTime = createdSession.startAt;
          }
          res = coachingSessionRes;
        }

        if (!wasMutationSuccessful(res)) {
          setSchedulingError(
            res.errors?.[0].extensions?.message as Optional<string>,
          );
          return;
        } else {
          onSchedule?.({
            id: newScheduledSessionId,
            startAt: newSessionStartTime,
          });
          setScheduledSessionId(newScheduledSessionId);
        }
      }
    } catch (error) {
      console.error('Error scheduling session:', error);
      setSchedulingError('An unexpected error occurred. Please try again.');
    } finally {
      setIsScheduling(false);
    }
  };

  // Trigger scheduling when component mounts and props are ready
  useEffect(() => {
    if (
      (schedulingData.selectedTime && schedulingData.sessionLength) ||
      isIntroCall
    ) {
      void performScheduling();
    } else {
      setIsScheduling(false);
      setSchedulingError(
        'Missing essential information to schedule the session.',
      );
    }
  }, [
    schedulingData.selectedTime,
    schedulingData.sessionLength,
    schedulingData.sessionType,
    isRescheduling,
    schedulingData.coach.id,
    schedulingData.message,
    schedulingData.categoryUrn,
    schedulingData.coachingSession,
    schedulingData.orderToAssign,
    schedulingData.justThisSession,
    schedulingData.recurringCadence,
    currentUser,
    createCoachingSessionByApplicantAction,
    updateCoachingSession,
    requestIntroMessageAction,
    assignPendingOrderAction,
  ]);

  return { isScheduling, schedulingError, scheduledSessionId };
};
